/**
 * Geeko PDP stay popup — show after staying on product page, once per day.
 */
(function () {
  const STORAGE = {
    DAILY_DATE: 'geeko_pdp_stay_popup_daily_date',
    SHOWN: 'geeko_pdp_stay_popup_shown',
    FLOATING: 'geeko_pdp_stay_popup_floating',
  };

  function getConfig() {
    return window.geekoPdpStayPopupConfig || {};
  }

  function getLocalDateKey() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function syncDailyStorage() {
    const today = getLocalDateKey();
    const storedDate = localStorage.getItem(STORAGE.DAILY_DATE);
    if (storedDate !== today) {
      localStorage.setItem(STORAGE.DAILY_DATE, today);
      localStorage.removeItem(STORAGE.SHOWN);
      localStorage.removeItem(STORAGE.FLOATING);
    }
  }

  function hasShownToday() {
    syncDailyStorage();
    return localStorage.getItem(STORAGE.SHOWN) === '1';
  }

  function recordShownToday() {
    localStorage.setItem(STORAGE.DAILY_DATE, getLocalDateKey());
    localStorage.setItem(STORAGE.SHOWN, '1');
  }

  function shouldShowFloating() {
    syncDailyStorage();
    return localStorage.getItem(STORAGE.FLOATING) === '1';
  }

  function markFloatingVisible() {
    localStorage.setItem(STORAGE.DAILY_DATE, getLocalDateKey());
    localStorage.setItem(STORAGE.FLOATING, '1');
  }

  class GeekoPdpStayPopup extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._stayTimer = null;
      this._onKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const config = getConfig();
      if (config.enabled === false) return;
      if (!this._isProductPage()) return;

      this._cacheDom();
      this._bindUi();

      if (shouldShowFloating()) {
        this._showFloating();
      }

      if (!hasShownToday()) {
        this._startStayTimer();
      }

      window.GeekoPdpStayPopup = this;
    }

    disconnectedCallback() {
      this._clearStayTimer();
      document.removeEventListener('keydown', this._onKeydown, true);
    }

    _isProductPage() {
      return document.body.classList.contains('page-product');
    }

    _cacheDom() {
      this._backdrop = this.querySelector('.geeko-pdp-stay-popup__backdrop');
      this._panel = this.querySelector('[data-geeko-pdp-stay-panel]');
      this._float = this.querySelector('[data-geeko-pdp-stay-float]');
    }

    _bindUi() {
      this.querySelectorAll('[data-geeko-pdp-stay-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this._onDismiss();
        });
      });

      this.querySelectorAll('[data-geeko-pdp-stay-continue]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this._onDismiss();
        });
      });

      this._float?.addEventListener('click', (e) => {
        e.preventDefault();
        this._openPopup({ source: 'float' });
      });
    }

    _getStayMs() {
      const seconds = parseInt(getConfig().staySeconds, 10);
      return (Number.isFinite(seconds) && seconds > 0 ? seconds : 30) * 1000;
    }

    _startStayTimer() {
      this._clearStayTimer();
      this._stayTimer = window.setTimeout(() => {
        this._stayTimer = null;
        if (hasShownToday() || this._isOpen) return;
        this._openPopup({ source: 'timer', auto: true });
        recordShownToday();
      }, this._getStayMs());
    }

    _clearStayTimer() {
      if (this._stayTimer != null) {
        clearTimeout(this._stayTimer);
        this._stayTimer = null;
      }
    }

    _openPopup(meta) {
      if (this._isOpen) return;

      this._isOpen = true;
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');

      if (this._panel) this._panel.hidden = false;
      this.classList.remove('has-float');
      if (this._float) {
        this._float.hidden = true;
        this._float.classList.remove('is-visible');
      }
      document.body.classList.add('overflow-hidden', 'geeko-pdp-stay-popup-open');
      document.addEventListener('keydown', this._onKeydown, true);

      this._publish('geeko_pdp_stay_popup_impression', meta || {});
    }

    _hidePopup() {
      if (!this._isOpen) return;

      this._isOpen = false;
      this.classList.remove('is-open');
      if (this._panel) this._panel.hidden = true;

      document.body.classList.remove('overflow-hidden', 'geeko-pdp-stay-popup-open');
      document.removeEventListener('keydown', this._onKeydown, true);
    }

    _onDismiss() {
      this._publish('geeko_pdp_stay_popup_close', {});
      markFloatingVisible();
      this._hidePopup();
      this._showFloating();
    }

    _showFloating() {
      if (!this._float) return;

      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('has-float');
      this._float.hidden = false;
      this._float.classList.add('is-visible');
    }

    _onKeydown(event) {
      if (!this._isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this._onDismiss();
      }
    }

    _publish(name, payload) {
      if (typeof Shopify !== 'undefined' && Shopify.analytics?.publish) {
        Shopify.analytics.publish(name, payload);
      }
    }
  }

  if (!customElements.get('geeko-pdp-stay-popup')) {
    customElements.define('geeko-pdp-stay-popup', GeekoPdpStayPopup);
  }
})();
